<template>
  <div>
    <pa-gantt-toolbar
      :selected-tasks="selectedTasks"
      @action="onToolbarAction"
    />

    <v-divider />

    <div style="position:relative">
      <pa-resizable
        class="pa-gantt"
        :min-width="350"
      >
        <!-- Left column - tasks info -->
        <div
          v-if="showTasksPane"
          class="pa-gantt__tasks"
        >
          <div class="pa-gantt__tasks-header" />

          <v-divider />

          <pa-gantt-tasks
            :tasks="tasksTree"
            :group-by="groupBy"
            :group-name="groupName"
            :selected-tasks="selectedTasks"
            :task-start="taskStart"
            :task-end="taskEnd"
            :task-duration="taskDuration"
            :task-name="taskName"
            @click="onClickTask($event, true)"
            @click-more="onCtrlClickTask"
            @add-task="reEmit('add-task', $event)"
            @edit-task="reEmit('edit-task', $event)"
            @expanded="onExpandTask"
          />
        </div>

        <!-- Right column - time axis and bars -->
        <div
          ref="chart"
          class="pa-gantt__chart"
          :style="showDrawer && !showTasksPane && 'margin-right:40%'"
        >
          <div
            ref="chartArea"
            :style="{ width: `${ganttWidth}px`, position: 'relative' }"
          >
            <!-- Time axis, grid and markers -->
            <pa-gantt-time-axis
              v-if="ganttStart && ganttEnd"
              :gantt-end="ganttEnd"
              :gantt-start="ganttStart"
              :grid-width="gridWidth"
              :zoom-level="ganttZoomLevel"
            />

            <!-- Task dependencies connections -->
            <pa-gantt-dependencies
              v-if="false"
              :dependencies="dependencies"
              :width="ganttWidth"
              :height="chartHeight"
            />

            <!-- Gantt bars -->
            <pa-gantt-row
              v-for="task in tasksTree.children"
              :key="`row-${task.id}`"
              :grid-width="gridWidth"
              :group-by="groupBy"
              :gantt-start="ganttStart"
              :task="task"
              :task-name="taskName"
              :task-progress="taskProgress"
              :zoom-level="ganttZoomLevel"
              @click="onClickTask"
            />
          </div>
        </div>
      </pa-resizable>

      <v-navigation-drawer
        :value="showDrawer"
        right
        fixed
        clipped
        hide-overlay
        stateless
        width="40%"
        :class="{ 'elevation-10': showDrawer }"
        style="margin-top:112px"
      >
        <slot
          name="drawer"
          :selected-activity="tasks.find(t => t.id === selectedTasks[0])"
          :close="closeDrawer"
        />
      </v-navigation-drawer>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { getProperty, sortBy } from '@/utils'
import { animateScroll } from '@/utils/animations'

import PaGanttDependencies from './PaGanttDependencies'
import PaGanttRow from './PaGanttRow'
import PaGanttTasks from './PaGanttTasks'
import PaGanttTimeAxis from './PaGanttTimeAxis'
import PaGanttToolbar from './PaGanttToolbar'
import PaResizable from '@/components/shared/PaResizable'

const ALL_ZOOM_LEVELS = [ 'day', 'week', 'month', 'quarter', 'year' ]

const MIN_GRID_WIDTHS = {
  day: 50,
  week: 100,
  month: 150,
  quarter: 200,
  year: 200
}

export default {
  name: 'PaGantt',
  components: {
    PaGanttDependencies,
    PaGanttRow,
    PaGanttTasks,
    PaGanttTimeAxis,
    PaGanttToolbar,
    PaResizable
  },
  props: {
    groupBy: {
      type: String,
      default: null
    },
    groupName: {
      type: String,
      default: 'group'
    },
    selected: {
      type: Array,
      default: () => []
    },
    taskDuration: {
      type: String,
      default: 'duration'
    },
    taskEnd: {
      type: String,
      default: 'dueDate'
    },
    taskName: {
      type: String,
      default: 'title'
    },
    taskParent: {
      type: String,
      default: 'parentId'
    },
    taskProgress: {
      type: String,
      default: 'progress'
    },
    taskStart: {
      type: String,
      default: 'startDate'
    },
    tasks: {
      type: Array,
      default: () => []
    },
    zoomLevel: {
      type: String,
      default: 'month',
      validator (value) {
        return ALL_ZOOM_LEVELS.includes(value)
      }
    }
  },
  data () {
    return {
      initialized: false,
      tasksTree: {},
      dependencies: [],
      chartHeight: 0,
      earliestDate: null,
      latestDate: null,
      ganttStart: null,
      ganttEnd: null,
      ganttWidth: 0,
      todayPositon: null,
      gridWidth: 0,
      ganttZoomLevel: 'month',
      selectedTasks: [],
      showTasksPane: true,
      showDrawer: false,

      // Panning handler
      isPanning: false,
      panningStartPosition: []
    }
  },
  watch: {
    tasks () {
      this.init()
    },
    zoomLevel (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.ganttZoomLevel = this.zoomLevel
        this.calculateGanttSizes()
      }
    }
  },
  mounted () {
    this.ganttZoomLevel = this.zoomLevel
    this.selectedTasks = [ ...this.selected ]
    this.init()
    this.initialized = true
  },
  beforeDestroy () {
    // panning listeners
    this.$refs.chart.removeEventListener('mousedown', this.onPanningStart, true)
    this.$refs.chart.removeEventListener('mousemove', this.onPanningMove, true)
    this.$refs.chart.removeEventListener('mouseup', this.onPanningEnd, true)
  },
  methods: {
    init () {
      this.earliestDate = Infinity
      this.latestDate = -Infinity
      this.dependencies = []

      const traverse = (tasks) => {
        tasks.sort(sortBy('order'))

        for (let task of tasks) {
          const start = getProperty(task, this.taskStart)
          const end = getProperty(task, this.taskEnd)
          const duration = getProperty(task, this.taskDuration)

          // Init task props
          if (start) {
            task.start = moment(start).startOf('day')
          }

          if (start && duration) {
            task.end = moment(task.start).add(duration, 'days')
          } else if (end) {
            task.end = moment(end)
          }

          if (task.successors) {
            for (let successor in task.successors) {
              this.dependencies.push([ task.id, successor ])
            }
          }

          // expand the task if there are not completed children
          this.$set(task, 'expanded',
            task.childrenCount && task.childrenCount[0] !== task.childrenCount[1])

          this.earliestDate = Math.min(this.earliestDate, +task.start || +task.end || Infinity)
          this.latestDate = Math.max(this.latestDate, +task.end || +task.start || -Infinity)

          if (task.children) {
            traverse(task.children)
          }
        }
      }

      if (this.groupBy) {
        this.tasksTree = this.buildTaskGroups()
      } else {
        this.tasksTree = this.buildTasksTree()
      }

      traverse(this.tasksTree.children)

      this.calculateGanttSizes()

      // Wait for html to be drawn
      this.$nextTick(() => {
        // recalculate height, as the chart inflates after drawing
        this.chartHeight = this.$refs.chartArea.offsetHeight

        this.initPanning()

        if (!this.initialized) {
          // scroll chart to today marker
          this.scrollToToday()
        }
      })
    },
    buildTaskGroups () {
      let rootTask = {
        id: null,
        parent: null,
        parentId: null,
        children: []
      }
      let map = {}

      for (let activity of this.tasks) {
        const groupIndex = map[getProperty(activity, this.groupBy)]

        if (groupIndex) {
          const group = rootTask.children[groupIndex]
          activity.parent = group
          group.children.push(activity)
        } else {
          // first time that I encounter this group
          map[getProperty(activity, this.groupBy)] = rootTask.children.length
          activity.children = []
          rootTask.children.push(activity)
        }
      }

      return rootTask
    },
    buildTasksTree () {
      let rootTask = {
        id: null,
        parent: null,
        parentId: null,
        children: []
      }
      let map = {}

      for (let i in this.tasks) {
        const activity = this.tasks[i]

        activity.children = []
        activity.childrenCount = [0, 0]
        map[activity.id] = i
      }

      for (let activity of this.tasks) {
        if (getProperty(activity, this.taskParent)) {
          const parent = this.tasks[map[getProperty(activity, this.taskParent)]]

          parent.childrenCount[0] += activity.childrenCount[0]
          parent.childrenCount[1] += (activity.childrenCount[1] + 1)
          if (activity.completed) { parent.childrenCount[0]++ }

          activity.parent = parent
          parent.children.push(activity)
        } else {
          // if it's top level then it's child of rootTask
          activity.parent = rootTask
          rootTask.children.push(activity)
        }
      }

      return rootTask
    },
    calculateGanttSizes () {
      /**
       * Gantt start/end date is the beginning/end of the period of the
       * earliest/latest task date (based on zoom level), i.e.:
       *  - Zoom level = month
       *  - Earliest task start date = 12 May 2018 => ganttStart = 1 May 2018
       *
       * NOTE: earliestDate and latestDate are numbers and can be Inf or -Inf
       *   if the tasks have no dates
       */
      this.ganttStart = (isFinite(this.earliestDate) ? moment(this.earliestDate) : moment())
        .startOf(this.ganttZoomLevel)
      this.ganttEnd = (isFinite(this.latestDate) ? moment(this.latestDate) : moment())
        .endOf(this.ganttZoomLevel)

      // number of time periods to show, e.g. 3 months, 12 weeks, ...
      const gridCount = Math.round(this.ganttEnd.diff(this.ganttStart, this.ganttZoomLevel, true)) || 0

      // make sure that the chart fills the container
      this.ganttWidth = Math.max(
        gridCount * MIN_GRID_WIDTHS[this.ganttZoomLevel],
        this.$refs.chart.offsetWidth // width of the container
      )

      this.gridWidth = this.ganttWidth / gridCount

      this.chartHeight = this.$refs.chartArea.offsetHeight
    },
    initPanning () {
      this.isPanning = false
      this.panningStartPosition = []

      this.$refs.chart.addEventListener('mousedown', this.onPanningStart, true)
      this.$refs.chart.addEventListener('mousemove', this.onPanningMove, true)
      this.$refs.chart.addEventListener('mouseup', this.onPanningEnd, true)
    },
    onPanningStart (event) {
      this.isPanning = true
      this.$refs.chart.classList.add('panning')

      this.panningStartPosition = [
        this.$refs.chart.scrollLeft + event.clientX,
        this.$refs.chart.scrollTop + event.clientY
      ]

      event.preventDefault()
    },
    onPanningMove (event) {
      event.preventDefault()

      if (this.isPanning) {
        let position = [
          event.clientX,
          event.clientY
        ]

        this.$refs.chart.scrollLeft = this.panningStartPosition[0] - position[0]
        this.$refs.chart.scrollTop = this.panningStartPosition[1] - position[1]
      }

      event.preventDefault()
    },
    onPanningEnd () {
      this.isPanning = false
      this.$refs.chart.classList.remove('panning')
    },
    onClickTask (task, scrollTo = false) {
      this.selectedTasks = [ task.id ]
      this.showDrawer = true
      this.$emit('click', task)
      this.$emit('update:selected', this.selectedTasks)

      if (scrollTo) {
        this.scrollToTask(task.id)
      }
    },
    onCtrlClickTask (task) {
      const i = this.selectedTasks.indexOf(task.id)

      if (i >= 0) {
        this.selectedTasks.splice(i, 1)
      } else {
        this.selectedTasks.push(task.id)
      }

      this.$emit('update:selected', this.selectedTasks)
    },
    onExpandTask () {
      setTimeout(this.calculateGanttSizes, 500)
    },
    onToolbarAction ({ action, data }) {
      switch (action) {
        case 'today':
          this.scrollToToday()
          break

        case 'zoom:out':
          this.changeZoom(+1)
          break

        case 'zoom:in':
          this.changeZoom(-1)
          break

        case 'link-tasks':
          this.$emit('link-tasks')
          break

        case 'indent:in':
          this.indentTask(true)
          break

        case 'indent:out':
          this.indentTask(false)
          break

        case 'show-tasks':
          this.showTasksPane = !this.showTasksPane
          this.$nextTick(this.calculateGanttSizes)
          break
      }
    },
    scrollToToday () {
      const to = moment().diff(this.ganttStart, this.ganttZoomLevel, true) *
        this.gridWidth -
        this.$refs.chart.offsetWidth / 2

      animateScroll(this.$refs.chart, to)
    },
    scrollToTask (taskId, duration = 400) {
      const taskEl = document.getElementById(`task-${taskId}`)
      // If the task has no start nor end date, there is no HTML el
      if (!taskEl) { return }

      const to = taskEl.offsetLeft - 20

      animateScroll(this.$refs.chart, to)
    },
    closeDrawer () {
      this.showDrawer = false
      this.$nextTick(this.calculateGanttSizes)
    },

    // Toolbar functions
    changeZoom (direction) {
      const currentLevel = ALL_ZOOM_LEVELS.indexOf(this.ganttZoomLevel)
      const newLevel = currentLevel + direction

      if (newLevel >= 0 && newLevel < ALL_ZOOM_LEVELS.length) {
        this.ganttZoomLevel = ALL_ZOOM_LEVELS[newLevel]
        this.$emit('update:zoom-level', this.ganttZoomLevel)
        this.$nextTick(this.calculateGanttSizes)
      }
    },
    async indentTask (indent = true) {
      const task = this.tasks.find(t => t.id === this.selectedTasks[0])
      let oldBranch
      let newBranch
      const oldOrder = task.order

      if (indent) {
        /**
         * [ Previous Sibling ]
         * [ Task to be indented ] =>
         */
        let prevSibling = task.parent.children[task.order - 1]

        // avoid indent if no prev sibling
        if (!task.order || !prevSibling || prevSibling.id === task.id) { return }

        oldBranch = task.parent.children

        task.parentId = prevSibling.id
        task.parent = prevSibling
        task.order = task.parent.children.length

        newBranch = task.parent.children
      } else {
        /**
         * [ Grand Parent ]
         *   [ Parent ]
         *     [ Other task 1 ] (this remains child of Parent)
         *     [ Task to be outdented ] <=
         *     [ Other task 2 ] (this remains child of Parent)
         *   [ Task to be outdented ] == New position
         */

        // it's a top level task, can't outdent
        if (!task.parent.parent) { return }

        let grandParent = task.parent.parent
        oldBranch = task.parent.children
        newBranch = grandParent.children

        const newOrder = task.parent.order + 1

        task.parentId = grandParent.id
        task.parent = grandParent
        task.order = newOrder
      }

      await task.save({ skipLog: true })

      newBranch.splice(task.order, 0, task)
      oldBranch.splice(oldOrder, 1)

      this.recalculateOrders(oldBranch)
      this.recalculateOrders(newBranch)
    },
    recalculateOrders (tasks) {
      tasks.forEach((task, i) => {
        task.order = i
      })

      tasks.forEach(task => {
        if (task.order !== task.$.order) {
          task.save({ skipLog: true })
        }
      })
    },
    reEmit (name, event) {
      this.$emit(name, event)
    }
  }
}
</script>

<style lang="scss">
@import "~styles/_vars.scss";

.pa-gantt {
  display: flex;
  position: relative;

  > * {
    box-sizing: border-box;
    flex: 1 1 auto;
  }

  &__tasks {
    min-width: 350px;
  }

  &__tasks-header {
    display: flex;
    align-items: center;
    height: $pa-gantt-task-height;

    > * {
      flex-grow: 1;
      flex-basis: 0;
    }
  }

  &__add-task {
    cursor: pointer;
    text-align: center;
    font-size: 90%;
    color: grey;

    &:hover {
      background: #f5f5f5;
    }

    &--dummy {
      cursor: inherit;

      &:hover {
        background: inherit;
      }
    }
  }

  // Right-side with gantt chart
  &__chart {
    position: relative;
    overflow-x: auto;
    overflow-y: hidden; // hide small white space at bottom of chart
    cursor: grab;

    &.panning {
      cursor: move;
    }
  }
}
</style>
